import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  collection,
  getFirestore,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import { authFetch } from '../utils/cloudFunctionsAuth';

// ---------------------------------------------------------------------------
// Recurring Stripe subscription arrangements — negotiated monthly support deals
// (variable amount + duration, often tiered: $350+GST/mo for 12 months, then
// $90/mo ongoing). Captured into the MAIN app Firestore by the Stripe webhook:
//   crm_subscriptions         — one per arrangement (status, term, phases)
//   crm_subscription_payments — one per paid monthly cycle
// Review-first: the owner LINKS each arrangement to a CRM customer, which
// backfills a PAID native invoice for every payment already received and makes
// every future payment auto-invoice. Server-only writes — the client reads and
// calls the link/unlink/dismiss endpoints.
// ---------------------------------------------------------------------------

const CF_BASE = 'https://us-central1-web-construct-app.cloudfunctions.net';

export type SubscriptionLinkStatus = 'unlinked' | 'linked' | 'dismissed';

/** A failing card. Written by the webhook from Stripe's dunning events; cleared
 *  automatically when a later charge succeeds. */
export interface SubscriptionDunning {
  failing: boolean;
  stripeInvoiceId: string;
  amountDue: number;
  currency: string;
  /** Stripe's own Smart-Retries counter. */
  attemptCount: number;
  nextAttemptAt: string | null;
  /** Stripe has GIVEN UP retrying — the point the owner must act. */
  exhausted: boolean;
  reason: string | null;
  failureCode: string | null;
  hostedInvoiceUrl: string | null;
  firstFailedAt: string | null;
  lastFailedAt: string | null;
  resolvedAt: string | null;
}

export interface SubscriptionPhase {
  iterations: number | null;   // null = open-ended (runs forever)
  iterationsDerived?: boolean; // true = computed from start/end dates
  startDate: string | null;
  endDate: string | null;
  priceId: string | null;
  amount: number | null;       // known only once that phase has billed
  quantity?: number;
}

export interface CrmSubscription {
  id: string;                          // == Stripe subscription id
  stripeSubscriptionId: string;
  stripeCustomerId: string | null;
  customerEmail: string;
  customerName: string;
  planName: string;
  planTier: string | null;
  status: string;                      // active | past_due | canceled | …
  currentAmount: number;               // inc-GST dollars per cycle
  currency: string;
  interval: string;                    // 'month' | 'year' | …
  intervalCount: number;
  currentPeriodEnd: string | null;     // YYYY-MM-DD — next charge
  cancelAtPeriodEnd: boolean;
  canceledAt: string | null;           // when cancellation was REQUESTED
  cancelAt: string | null;             // future auto-cancel date
  endedAt: string | null;              // when it actually stopped
  /** True only when someone asked to stop — NOT when an agreed term runs out. */
  cancelledByCustomer: boolean;
  /** Agreed end of a fixed-term schedule (end_behavior 'cancel'). */
  scheduledEndsAt: string | null;
  scheduleId: string | null;
  phases: SubscriptionPhase[];
  initialTermIterations: number | null; // e.g. 12 — null = open-ended
  paymentsMade: number;
  lastPaymentAt: string | null;
  lastPaymentAmount: number | null;
  // --- mirrored from the website's negotiated arrangement -------------------
  arrangementId: string | null;
  arrangementStatus: string | null;
  /** The owner's own reference for the deal ("Reference (optional)"). */
  clientRef: string | null;
  arrangementDescription: string | null;
  gstTreatment: 'exclusive' | 'inclusive';
  arrangementLineItems: { description?: string; qty?: number; unitCents?: number }[] | null;
  phase1AmountExGst: number | null;
  phase1AmountIncGst: number | null;
  phase2AmountExGst: number | null;
  phase2AmountIncGst: number | null;
  /** Agreed cycles at the opening rate — authoritative over date inference. */
  termCycles: number | null;
  /** Opening term is a committed minimum (we may withdraw up-front work). */
  committedTerm: boolean;
  /** Set when the step-down schedule couldn't attach — billing stays on the
   *  opening rate, so this needs the owner's attention. */
  scheduleError: string | null;
  /** A declined card, or null when billing is healthy. */
  dunning: SubscriptionDunning | null;
  lastPaymentFailedAt: string | null;
  testMode: boolean;
  linkStatus: SubscriptionLinkStatus;
  linkedCustomerId: string | null;
  linkedCustomerName: string | null;
  createdAt?: Timestamp;
}

export interface SubscriptionPayment {
  id: string;                          // == Stripe invoice id
  subscriptionId: string;
  amount: number;
  currency: string;
  email: string;
  name: string;
  description: string;
  paidAt: string;                      // YYYY-MM-DD (Adelaide)
  periodStart: string | null;
  periodEnd: string | null;
  billingReason: string;
  invoiceNumber: string | null;        // Stripe's own invoice number
  hostedInvoiceUrl: string | null;
  testMode: boolean;
  nativeInvoiceId: string | null;      // our generated PAID invoice
  nativeInvoiceNumber: string | null;
  /** 1 = opening rate, 2 = stepped-down rate (determined by Stripe price id). */
  arrangementPhase: 1 | 2 | null;
  itemisedFromArrangement: boolean;
  /** Why this payment could NOT be invoiced (e.g. totals disagree with Stripe). */
  invoiceError: string | null;
  /** Refunded back to the customer (a credit note + negative ledger row). */
  refundedAmount: number;
  refundedAt: string | null;
  fullyRefunded: boolean;
  creditNoteId: string | null;
  createdAt?: Timestamp;
}

interface ActionResult { ok: boolean; error?: string; customerName?: string; invoicesCreated?: number }

interface SubscriptionsContextType {
  subscriptions: CrmSubscription[];
  payments: SubscriptionPayment[];
  /** Arrangements awaiting an owner decision (not yet linked or dismissed). */
  pending: CrmSubscription[];
  /** Linked + still billing. */
  active: CrmSubscription[];
  loading: boolean;
  /** Set when the listeners could not read (e.g. a non-admin is denied). The UI
   *  MUST distinguish this from "no arrangements yet" — they look identical
   *  otherwise, and an empty page reads as healthy. */
  error: string | null;
  paymentsFor: (subscriptionId: string) => SubscriptionPayment[];
  linkSubscription: (subscriptionId: string, customerId: string) => Promise<ActionResult>;
  unlinkSubscription: (subscriptionId: string) => Promise<ActionResult>;
  dismissSubscription: (subscriptionId: string) => Promise<ActionResult>;
}

const SubscriptionsContext = createContext<SubscriptionsContextType>({
  subscriptions: [],
  payments: [],
  pending: [],
  active: [],
  loading: true,
  error: null,
  paymentsFor: () => [],
  linkSubscription: async () => ({ ok: false }),
  unlinkSubscription: async () => ({ ok: false }),
  dismissSubscription: async () => ({ ok: false }),
});

function mapSubscription(id: string, d: any): CrmSubscription {
  return {
    id,
    stripeSubscriptionId: String(d?.stripeSubscriptionId || id),
    stripeCustomerId: d?.stripeCustomerId ?? null,
    customerEmail: String(d?.customerEmail || ''),
    customerName: String(d?.customerName || ''),
    planName: String(d?.planName || ''),
    planTier: d?.planTier ?? null,
    status: String(d?.status || 'active'),
    currentAmount: Number(d?.currentAmount) || 0,
    currency: String(d?.currency || 'AUD'),
    interval: String(d?.interval || 'month'),
    intervalCount: Number(d?.intervalCount) || 1,
    currentPeriodEnd: d?.currentPeriodEnd ?? null,
    cancelAtPeriodEnd: d?.cancelAtPeriodEnd === true,
    canceledAt: d?.canceledAt ?? null,
    cancelAt: d?.cancelAt ?? null,
    endedAt: d?.endedAt ?? null,
    // Prefer the stored flag; fall back to the raw signals for rows captured
    // before the flag existed (they only refresh on the next Stripe event).
    cancelledByCustomer: d?.cancelledByCustomer === true
      || d?.cancelAtPeriodEnd === true
      || d?.canceledAt != null,
    scheduledEndsAt: d?.scheduledEndsAt ?? null,
    scheduleId: d?.scheduleId ?? null,
    phases: Array.isArray(d?.phases) ? d.phases : [],
    initialTermIterations: d?.initialTermIterations ?? null,
    paymentsMade: Number(d?.paymentsMade) || 0,
    lastPaymentAt: d?.lastPaymentAt ?? null,
    lastPaymentAmount: d?.lastPaymentAmount ?? null,
    arrangementId: d?.arrangementId ?? null,
    arrangementStatus: d?.arrangementStatus ?? null,
    clientRef: d?.clientRef ?? null,
    arrangementDescription: d?.arrangementDescription ?? null,
    gstTreatment: d?.gstTreatment === 'inclusive' ? 'inclusive' : 'exclusive',
    arrangementLineItems: Array.isArray(d?.arrangementLineItems) ? d.arrangementLineItems : null,
    phase1AmountExGst: d?.phase1AmountExGst ?? null,
    phase1AmountIncGst: d?.phase1AmountIncGst ?? null,
    phase2AmountExGst: d?.phase2AmountExGst ?? null,
    phase2AmountIncGst: d?.phase2AmountIncGst ?? null,
    termCycles: d?.termCycles ?? null,
    committedTerm: d?.committedTerm === true,
    scheduleError: d?.scheduleError ?? null,
    dunning: d?.dunning && typeof d.dunning === 'object' ? {
      failing: d.dunning.failing === true,
      stripeInvoiceId: String(d.dunning.stripeInvoiceId || ''),
      amountDue: Number(d.dunning.amountDue) || 0,
      currency: String(d.dunning.currency || 'AUD'),
      attemptCount: Number(d.dunning.attemptCount) || 0,
      nextAttemptAt: d.dunning.nextAttemptAt ?? null,
      exhausted: d.dunning.exhausted === true,
      reason: d.dunning.reason ?? null,
      failureCode: d.dunning.failureCode ?? null,
      hostedInvoiceUrl: d.dunning.hostedInvoiceUrl ?? null,
      firstFailedAt: d.dunning.firstFailedAt ?? null,
      lastFailedAt: d.dunning.lastFailedAt ?? null,
      resolvedAt: d.dunning.resolvedAt ?? null,
    } : null,
    lastPaymentFailedAt: d?.lastPaymentFailedAt ?? null,
    testMode: d?.testMode === true,
    linkStatus: (d?.linkStatus as SubscriptionLinkStatus) || 'unlinked',
    linkedCustomerId: d?.linkedCustomerId ?? null,
    linkedCustomerName: d?.linkedCustomerName ?? null,
    createdAt: d?.createdAt,
  };
}

function mapPayment(id: string, d: any): SubscriptionPayment {
  return {
    id,
    subscriptionId: String(d?.subscriptionId || ''),
    amount: Number(d?.amount) || 0,
    currency: String(d?.currency || 'AUD'),
    email: String(d?.email || ''),
    name: String(d?.name || ''),
    description: String(d?.description || ''),
    paidAt: String(d?.paidAt || ''),
    periodStart: d?.periodStart ?? null,
    periodEnd: d?.periodEnd ?? null,
    billingReason: String(d?.billingReason || ''),
    invoiceNumber: d?.invoiceNumber ?? null,
    hostedInvoiceUrl: d?.hostedInvoiceUrl ?? null,
    testMode: d?.testMode === true,
    nativeInvoiceId: d?.nativeInvoiceId ?? null,
    nativeInvoiceNumber: d?.nativeInvoiceNumber ?? null,
    refundedAmount: Number(d?.refundedAmount) || 0,
    refundedAt: d?.refundedAt ?? null,
    fullyRefunded: d?.fullyRefunded === true,
    creditNoteId: d?.creditNoteId ?? null,
    arrangementPhase: d?.arrangementPhase === 2 ? 2 : d?.arrangementPhase === 1 ? 1 : null,
    itemisedFromArrangement: d?.itemisedFromArrangement === true,
    invoiceError: d?.invoiceError ?? null,
    createdAt: d?.createdAt,
  };
}

/** POST a { subscriptionId, … } action and normalise the response. */
async function postAction(path: string, body: Record<string, any>, fallbackMsg: string): Promise<ActionResult> {
  try {
    const res = await authFetch(`${CF_BASE}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!data.ok) return { ok: false, error: data.error || fallbackMsg };
    return { ok: true, customerName: data.customerName, invoicesCreated: data.invoicesCreated };
  } catch (e: any) {
    return { ok: false, error: e?.message || fallbackMsg };
  }
}

export const SubscriptionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscriptions, setSubscriptions] = useState<CrmSubscription[]>([]);
  const [payments, setPayments] = useState<SubscriptionPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const subsLoaded = useRef(false);
  const paysLoaded = useRef(false);

  useEffect(() => {
    let unsubSubs: (() => void) | null = null;
    let unsubPays: (() => void) | null = null;
    const markLoaded = () => {
      if (subsLoaded.current && paysLoaded.current) setLoading(false);
    };

    try {
      const db = getFirestore();

      unsubSubs = onSnapshot(
        query(collection(db, 'crm_subscriptions'), orderBy('createdAt', 'desc')),
        (snap) => {
          setSubscriptions(snap.docs.map((d) => mapSubscription(d.id, d.data())));
          if (!subsLoaded.current) { subsLoaded.current = true; markLoaded(); return; }
          // Toast only genuinely new arrangements awaiting a decision.
          snap.docChanges().forEach((c) => {
            if (c.type !== 'added') return;
            const s = mapSubscription(c.doc.id, c.doc.data());
            if (s.linkStatus !== 'unlinked') return;
            const who = s.customerName || s.customerEmail || 'a customer';
            toast.info(
              `🔁 New recurring arrangement — ${who}${s.testMode ? ' (TEST)' : ''}. Link it to a customer.`,
              { autoClose: 9000 },
            );
          });
        },
        (err) => { console.warn('[Subscriptions] subs listener error:', err); setError(err?.message || 'Could not read arrangements'); subsLoaded.current = true; markLoaded(); },
      );

      unsubPays = onSnapshot(
        query(collection(db, 'crm_subscription_payments'), orderBy('paidAt', 'desc')),
        (snap) => {
          setPayments(snap.docs.map((d) => mapPayment(d.id, d.data())));
          if (!paysLoaded.current) { paysLoaded.current = true; markLoaded(); return; }
          snap.docChanges().forEach((c) => {
            if (c.type !== 'added') return;
            const p = mapPayment(c.doc.id, c.doc.data());
            if (p.testMode) return;
            toast.success(
              `💰 Recurring payment received — $${p.amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`,
              { autoClose: 8000 },
            );
          });
        },
        (err) => { console.warn('[Subscriptions] payments listener error:', err); setError(err?.message || 'Could not read payments'); paysLoaded.current = true; markLoaded(); },
      );
    } catch (err) {
      console.warn('[Subscriptions] init failed:', err);
      setLoading(false);
    }

    return () => { if (unsubSubs) unsubSubs(); if (unsubPays) unsubPays(); };
  }, []);

  const paymentsBySub = useMemo(() => {
    const m = new Map<string, SubscriptionPayment[]>();
    for (const p of payments) {
      const arr = m.get(p.subscriptionId) || [];
      arr.push(p);
      m.set(p.subscriptionId, arr);
    }
    // Newest first within each subscription.
    for (const arr of Array.from(m.values())) {
      arr.sort((a, b) => String(b.paidAt).localeCompare(String(a.paidAt)));
    }
    return m;
  }, [payments]);

  const paymentsFor = useCallback(
    (subscriptionId: string) => paymentsBySub.get(subscriptionId) || [],
    [paymentsBySub],
  );

  const linkSubscription = useCallback(
    (subscriptionId: string, customerId: string) =>
      postAction('linkSubscriptionHttp', { subscriptionId, customerId }, 'Could not link subscription'),
    [],
  );
  const unlinkSubscription = useCallback(
    (subscriptionId: string) =>
      postAction('unlinkSubscriptionHttp', { subscriptionId }, 'Could not unlink subscription'),
    [],
  );
  const dismissSubscription = useCallback(
    (subscriptionId: string) =>
      postAction('dismissSubscriptionHttp', { subscriptionId }, 'Could not dismiss subscription'),
    [],
  );

  const pending = useMemo(() => subscriptions.filter((s) => s.linkStatus === 'unlinked'), [subscriptions]);
  const active = useMemo(
    () => subscriptions.filter((s) => s.linkStatus === 'linked' && s.status !== 'canceled'),
    [subscriptions],
  );

  const value = useMemo(
    () => ({
      subscriptions, payments, pending, active, loading, error,
      paymentsFor, linkSubscription, unlinkSubscription, dismissSubscription,
    }),
    [subscriptions, payments, pending, active, loading, error, paymentsFor, linkSubscription, unlinkSubscription, dismissSubscription],
  );

  return <SubscriptionsContext.Provider value={value}>{children}</SubscriptionsContext.Provider>;
};

export const useSubscriptions = () => useContext(SubscriptionsContext);

// ---------------------------------------------------------------------------
// Shared term helpers — "3 of 12 paid · 9 left, then $90/mo" logic used by both
// the Recurring Payments page and the per-customer tab.
// ---------------------------------------------------------------------------

export interface TermInfo {
  /** True when the deal has a fixed initial term (vs open-ended). */
  fixedTerm: boolean;
  total: number | null;      // e.g. 12
  paid: number;              // payments received so far
  remaining: number | null;  // e.g. 9
  /** The step-down phase (if the schedule has one). */
  nextPhaseAmount: number | null;
  nextPhaseOngoing: boolean;
  label: string;             // human summary
}

/**
 * Why (and when) an arrangement stops. An end date ALONE is ambiguous — the same
 * "ends 28 Aug" can mean the customer walked away (chase it) or the agreed term
 * finishing exactly as sold (do nothing). Stripe only sets cancel_at_period_end /
 * canceled_at when someone actually ASKED to stop, so that's the discriminator;
 * a schedule's own end date is a term completion, not a cancellation.
 */
export interface EndInfo {
  /** null = no end in sight (open-ended and running). */
  endsAt: string | null;
  reason: 'cancelled' | 'term' | null;
  /** Already finished (not just scheduled to). */
  ended: boolean;
  label: string;
  /** Bootstrap tone for the pill. */
  tone: 'warning' | 'secondary' | 'success';
}

const fmtDay = (ymd: string | null): string => {
  if (!ymd) return '';
  const d = new Date(`${ymd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

export function endInfo(s: CrmSubscription): EndInfo {
  const finished = s.status === 'canceled' || !!s.endedAt;

  if (s.cancelledByCustomer) {
    // cancelAt/currentPeriodEnd = when billing actually stops (they keep the
    // period they've already paid for); endedAt if it's already done.
    const when = s.endedAt || s.cancelAt || s.currentPeriodEnd || s.canceledAt;
    return {
      endsAt: when,
      reason: 'cancelled',
      ended: finished,
      // Deliberately not "customer cancelled" — Stripe records THAT a cancellation
      // was requested, never by whom (portal, dashboard and API all look the same).
      // Claiming the client walked away when you cancelled it would misinform.
      label: finished
        ? `Cancelled${when ? ` · ended ${fmtDay(when)}` : ''}`
        : `Cancelled · billing stops ${fmtDay(when)}`,
      tone: 'warning',
    };
  }

  const termEnd = s.scheduledEndsAt || s.cancelAt;
  if (termEnd) {
    return {
      endsAt: termEnd,
      reason: 'term',
      ended: finished,
      label: finished
        ? `Agreed term completed ${fmtDay(termEnd)}`
        : `Agreed term ends ${fmtDay(termEnd)} · not a cancellation`,
      tone: finished ? 'secondary' : 'success',
    };
  }

  if (finished) {
    return {
      endsAt: s.endedAt,
      reason: null,
      ended: true,
      label: s.endedAt ? `Ended ${fmtDay(s.endedAt)}` : 'Ended',
      tone: 'secondary',
    };
  }
  return { endsAt: null, reason: null, ended: false, label: '', tone: 'success' };
}

/**
 * A declined card, phrased for the owner. `status` stays the state machine and
 * `dunning` is the explanation — never derive one from the other, since the two
 * Stripe event streams race.
 */
export function dunningInfo(
  s: CrmSubscription,
): { label: string; tone: 'danger' | 'warning'; urgent: boolean } | null {
  const d = s.dunning;
  if (!d?.failing) return null;
  const why = d.reason ? ` (${d.reason})` : '';
  if (d.exhausted) {
    return {
      label: `Card declined — Stripe has stopped retrying${why}`,
      tone: 'danger',
      urgent: true,
    };
  }
  return {
    label: `Card declined · attempt ${d.attemptCount}${d.nextAttemptAt ? ` · retries ${fmtDay(d.nextAttemptAt)}` : ''}${why}`,
    tone: 'warning',
    urgent: false,
  };
}

export function termInfo(s: CrmSubscription): TermInfo {
  // The negotiated `termCycles` wins over anything inferred from Stripe's phase
  // dates — it's what was actually agreed with the customer.
  const total = s.termCycles ?? s.initialTermIterations;
  const paid = s.paymentsMade || 0;
  const fixedTerm = !!total && total > 0;
  const remaining = fixedTerm ? Math.max(0, (total as number) - paid) : null;

  const laterPhase = s.phases.length > 1 ? s.phases[1] : null;
  // Stripe only sends a price ID for future phases, so the dollar figure comes
  // from the arrangement — that's how we can say "then $99/mo" up front.
  const nextPhaseAmount = s.phase2AmountIncGst ?? laterPhase?.amount ?? null;
  const nextPhaseOngoing = (!!laterPhase && !laterPhase.iterations) || s.phase2AmountIncGst != null;

  let label: string;
  if (fixedTerm) {
    label = `${paid} of ${total} paid · ${remaining} left`;
    if (laterPhase || nextPhaseAmount != null) {
      label += nextPhaseAmount != null
        ? `, then $${nextPhaseAmount.toLocaleString('en-AU')}/${s.interval}`
        : ', then steps down';
    }
  } else {
    label = paid > 0 ? `${paid} payment${paid === 1 ? '' : 's'} · ongoing` : 'Ongoing';
  }

  return { fixedTerm, total, paid, remaining, nextPhaseAmount, nextPhaseOngoing, label };
}
